// @ts-nocheck
// TypeScript is ignoring this file. If you are editing this file you must fix the type issues.

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { LiliButton } from '@lili/lili-frontend-common';
import { ReactComponent as EditIcon } from '../../../assets/svg/edit-pen.svg';
import Heading from '../../../generic-components/Heading';
import {
  getAnalyticsFlowName,
  getVendorDetailsBlocks,
  isRequireDataForVendorValid,
  MoveMoneySteps,
  MoveMoneyTypes,
  reviewContentMap,
} from '../consts';
import { VIEWS_MAP } from '../../../router/constants';
import { convertValueToUSD } from '../../../helpers/currency.helper';
import Button from '../../../generic-components/Button';
import { LiliTextArea } from '../../../DesignSystem/LiliTextArea/LiliTextArea';
import { GENERIC_FILE_UPLOAD_TYPES, IMoveMoneyReviewResponse, ISummaryData } from '../../../types';
import { NavigationHeader } from '../components/NavigationHeader';
import { trackMixPanel } from '../../../services/mixpanel.service';
import { EditVendorMenu } from '../components/review/EditVendorMenu';
import { useMoveMoneyLogic } from '../hooks/useMoveMoneyLogic';
import { AchOutFeeSelector } from '../components/review/AchOutFeeSelector';
import useStore from '../../../store';
import { useDebounceHandler } from '../../../hooks/utilities/useDebounceHandler';

export enum TRANSFER_TYPES {
  EXPRESS = 'EXPRESS',
  STANDARD = 'STANDARD',
}

export const Review = () => {
  const navigate = useNavigate();
  const { moveType } = useParams();
  const { t } = useTranslation();
  const [achOutTransferType, setAchOutTransferType] = useState<TRANSFER_TYPES>(TRANSFER_TYPES.STANDARD);
  const { debounceAction } = useDebounceHandler(1000);

  /** Queries */
  const {
    moveMoneyData,
    setMoveMoneyData,
    getMoveMoneyData,
    resetMoveMoneyData,
    postWireReview,
    postMailCheck,
    postUploadSnapShot,
    isLoadingState,
    fetchAchOutFees,
    postInternationalWireReview,
    postAchOut,
  } = useMoveMoneyLogic();
  /** ------- */

  const [transferNote, setTransferNote] = useState<string>(moveMoneyData.transferNote || '');
  const summaryData: ISummaryData = useStore((state) => state.summary);

  const getSelectedAchFee = () => {
    const { achOutFeeData } = moveMoneyData;
    if (!achOutFeeData) return undefined;
    return achOutFeeData?.fees.find((feeItem) => feeItem.achTransferType === achOutTransferType);
  };

  const achOutActiveFeeData = useMemo(() => getSelectedAchFee(), [moveMoneyData, achOutTransferType]);

  const handleCloseFlow = () => {
    resetMoveMoneyData();
    navigate(`/${VIEWS_MAP.DASHBOARD}`);
  };

  const handleReviewResponse = (mfaData: IMoveMoneyReviewResponse) => {
    if (mfaData.status === 'MFA_CHALLENGE') {
      setMoveMoneyData({ ...moveMoneyData, mfaChallengeData: mfaData, activeStepId: MoveMoneySteps.MFA });
    } else {
      setMoveMoneyData({ ...moveMoneyData, mfaChallengeData: mfaData, activeStepId: MoveMoneySteps.SUCCESS });
    }
  };

  const handleContinue = async () => {
    trackMixPanel('Act on Move Money Review Screen', {
      Flow: getAnalyticsFlowName(moveType as MoveMoneyTypes),
      Action: 'Confirm',
    });
    if (transferNote) {
      trackMixPanel('Act on Move Money Review Screen', {
        Flow: getAnalyticsFlowName(moveType as MoveMoneyTypes),
        Action: 'Note Added',
      });
    }
    if (moveType === MoveMoneyTypes.WIRE_OUT) {
      await postUploadSnapShot({
        fileUsageType: GENERIC_FILE_UPLOAD_TYPES.WIRE_OUT,
        imageName: `${Date.now()}`,
      });
      const res = await postWireReview({
        amount: moveMoneyData.amount,
        supplierId: moveMoneyData.vendorData?.supplierId || '',
        note: transferNote,
        billId: moveMoneyData.billId,
      });
      handleReviewResponse(res);
    } else if (moveType === MoveMoneyTypes.MAIL_CHECK) {
      await postUploadSnapShot({
        fileUsageType: GENERIC_FILE_UPLOAD_TYPES.MAIL_A_CHECK,
        imageName: `${Date.now()}`,
      });
      const res = await postMailCheck({
        amount: moveMoneyData.amount,
        supplierId: moveMoneyData.vendorData?.supplierId || '',
        memo: transferNote,
        billId: moveMoneyData.billId,
      });
      handleReviewResponse(res);
    } else if (moveType === MoveMoneyTypes.INTERNATIONAL_WIRE_OUT) {
      await postUploadSnapShot({
        fileUsageType: GENERIC_FILE_UPLOAD_TYPES.INTL_WIRE_OUT,
        imageName: `${Date.now()}`,
      });
      const res = await postInternationalWireReview({
        amount: moveMoneyData.amount,
        billId: moveMoneyData?.billId || '',
        supplierId: moveMoneyData.vendorData?.supplierId || '',
        reason: moveMoneyData.purposeOfTransfer || '',
        reasonDescription: moveMoneyData.purposeOfTransferReason || '',
      });
      handleReviewResponse(res);
    } else if (moveType === MoveMoneyTypes.ACH_OUT_TRANSFER) {
      await postUploadSnapShot({
        fileUsageType: GENERIC_FILE_UPLOAD_TYPES.ACH_OUT,
        imageName: `${Date.now()}`,
      });
      const res = await postAchOut({
        formData: {
          businessName: moveMoneyData.vendorData?.name || '',
          accountNumber: moveMoneyData.vendorData?.achAccountData?.accountNumber || '',
          routingNumber: moveMoneyData.vendorData?.achAccountData?.routingNumber || '',
          transferAmount: moveMoneyData.amount.toString() || '0',
          feeData: {
            achTransferType: achOutActiveFeeData?.achTransferType || '',
            arrivalDate: achOutActiveFeeData?.arrivalDate || '',
            arrivalDateShort: achOutActiveFeeData?.arrivalDateShort || '',
            fee: achOutActiveFeeData?.fee || 0,
          },
        },
        billId: moveMoneyData.billId,
        supplierId: moveMoneyData.vendorData?.supplierId || '',
      });
      handleReviewResponse(res);
    }
  };

  const handleEditAmount = () => {
    trackMixPanel('Act on Move Money Review Screen', {
      Flow: getAnalyticsFlowName(moveType as MoveMoneyTypes),
      Action: 'Edit Amount',
    });
    setMoveMoneyData({ ...moveMoneyData, activeStepId: MoveMoneySteps.AMOUNT, isEditMode: true });
  };

  const handleEditVendor = () => {
    trackMixPanel('Act on Move Money Review Screen', {
      Flow: getAnalyticsFlowName(moveType as MoveMoneyTypes),
      Action: 'Edit Payee',
    });
    setMoveMoneyData({ ...moveMoneyData, activeStepId: MoveMoneySteps.VENDOR_UPDATE, isEditMode: true });
  };

  const handleSwitchVendor = () => {
    trackMixPanel('Act on Move Money Review Screen', {
      Flow: getAnalyticsFlowName(moveType as MoveMoneyTypes),
      Action: 'Switch Payee',
    });
    setMoveMoneyData({ ...moveMoneyData, activeStepId: MoveMoneySteps.VENDOR_PICKER, isEditMode: true });
  };

  const isValidForAchOut = useMemo(() => {
    if (moveType === MoveMoneyTypes.ACH_OUT_TRANSFER) {
      return getMoveMoneyData().achOutFeeData?.fees.length > 0;
    }
    return true;
  }, [moveMoneyData]);

  const checkIfConfirmValid = useMemo(
    () => !!moveMoneyData.amount && !!moveMoneyData.vendorData && transferNote.length <= 50,
    [moveMoneyData, transferNote],
  );

  useEffect(() => {
    if (!isRequireDataForVendorValid(moveMoneyData.vendorData, moveType as MoveMoneyTypes)) {
      setMoveMoneyData({
        ...moveMoneyData,
        isEditMode: true,
        activeStepId: MoveMoneySteps.VENDOR_UPDATE,
        isVendorUpdateMandatory: true,
      });
    } else {
      trackMixPanel('View Move Money Review Screen', { Flow: getAnalyticsFlowName(moveType as MoveMoneyTypes) });
      setMoveMoneyData({ ...moveMoneyData, isEditMode: true });
      if (moveType === MoveMoneyTypes.ACH_OUT_TRANSFER) {
        fetchAchOutFees().then((achOutFeeResponse) => {
          const { data: achOutFees } = achOutFeeResponse;
          setMoveMoneyData({ ...moveMoneyData, achOutFeeData: achOutFees });
        });
      }
    }
  }, []);

  const shouldShowMemo = moveType === MoveMoneyTypes.WIRE_OUT || moveType === MoveMoneyTypes.MAIL_CHECK;

  useEffect(() => {
    setMoveMoneyData({ ...getMoveMoneyData(), achOutTransferType });
  }, [achOutTransferType]);
  const name = `${summaryData.customer.firstName}  ${summaryData.customer.lastName}`;

  const handleBack = () => {
    setMoveMoneyData({
      ...moveMoneyData,
      activeStepId: moveMoneyData.eddRequired
        ? MoveMoneySteps.INTERNATIONAL_WIRE_EDD
        : MoveMoneySteps.PURPOSE_OF_TRANSFER,
    });
  };

  const syncTransferNote = (isClear?: boolean) => {
    setMoveMoneyData({ ...getMoveMoneyData(), transferNote: isClear ? '' : transferNote });
  };

  return (
    <div className='fixed left-0 top-0 w-full h-full bg-bw00 z-50 flex flex-col'>
      <NavigationHeader
        hideBackButton={moveType !== MoveMoneyTypes.INTERNATIONAL_WIRE_OUT}
        onBack={handleBack}
        onClose={handleCloseFlow}
      />
      <div className='overflow-auto no-scroll pb-[150px]'>
        <Heading
          type='HTML'
          title={t(reviewContentMap[moveType as keyof typeof reviewContentMap].content)}
          className='mx-auto text-center mb-[16px] font-metricR text-bw08 text-[24px]'
        />
        <div className='py-[16px] px-[16px] rounded-[8px] border border-bw03 w-[432px] mx-auto mb-[10px]'>
          <div className='flex justify-between'>
            <Heading
              title={t('MOVE_MONEY.STEPS.REVIEW.TRANSFER_DETAILS')}
              className='font-metricR mb-[14px] text-bw08 text-[18px]'
            />
            {!moveMoneyData.billId && (
              <Button onClick={handleEditAmount} className='' id='editAmount' childElement={<EditIcon />} />
            )}
          </div>

          <div className='flex items-center justify-between w-full pb-[16px] border-b border-bw03 mb-[16px]'>
            <div className='flex flex-col w-full'>
              <div className='flex items-center gap-[6px] justify-between'>
                <Heading title={t('MOVE_MONEY.STEPS.REVIEW.AMOUNT')} className='font-metricR text-bw07 text-[16px]' />

                <div className='flex flex-col'>
                  <Heading
                    id='amountToReview'
                    title={convertValueToUSD(moveMoneyData.amount, 2)}
                    className='font-metricRMedium text-action03 text-[22px]'
                  />
                </div>
              </div>
              {moveType !== MoveMoneyTypes.INTERNATIONAL_WIRE_OUT && (
                <div className='flex justify-between mt-[10px]'>
                  <Heading title='From' className='font-metricR text-bw07 text-[16px] items-center' />
                  <div className='flex flex-col items-end'>
                    <Heading title={name} className=' text-bw08 text-md' />
                    <Heading
                      title={`${t('MOVE_MONEY.STEPS.INTRO.ACH_OUT.LILI_CHECKING_ACCOUNT')}`}
                      className='font-metricR text-bw07 text-md'
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className='flex items-center justify-between mb-[10px]'>
            <Heading
              title={t(reviewContentMap[moveType as keyof typeof reviewContentMap].dynamic_blocks_title)}
              className='font-metricR text-bw08 text-[18px]'
            />
            {!moveMoneyData.billId && (
              <EditVendorMenu handleEditVendor={handleEditVendor} handleSwitchVendor={handleSwitchVendor} />
            )}
          </div>
          <div className='flex flex-col gap-[10px]'>
            {getVendorDetailsBlocks(moveType as MoveMoneyTypes, moveMoneyData).map((item) => (
              <div className='flex items-start justify-between'>
                <Heading title={t(item.title)} className='font-metricR text-bw07 text-[16px]' />
                <Heading
                  id={item?.id || ''}
                  title={t(item.value)}
                  className='font-metricR max-w-[200px] break-all text-wrap text-right text-bw08 text-[14px]'
                />
              </div>
            ))}
          </div>
        </div>

        {moveType !== MoveMoneyTypes.ACH_OUT_TRANSFER && (
          <div className='py-[16px] px-[16px] rounded-[8px] border border-bw03 w-[432px] mx-auto mb-[44px] relative'>
            {moveMoneyData.feeData?.fees?.[0]?.arrivalDate && (
              <div className='flex items-center justify-between border-b border-bw03 pb-[12px] mb-[12px]'>
                <Heading title='Estimated delivery' className='font-metricR text-bw07 text-[14px]' />
                <Heading
                  id='estimateArrivalValue'
                  title={moveMoneyData.feeData.fees[0].arrivalDate}
                  className='font-metricR text-bw08 text-[16px]'
                />
              </div>
            )}
            <div className='flex items-center justify-between'>
              <Heading title='Fee' className='font-metricR text-bw07 text-[16px]' />
              <Heading
                id='feeValue'
                title={convertValueToUSD(moveMoneyData.feeData?.fees?.[0]?.fee || 0, 2)}
                className='font-metricR text-bw08 text-[16px]'
              />
            </div>
            <Heading
              title={t(reviewContentMap[moveType as keyof typeof reviewContentMap].disclaimer)}
              className='font-metricR text-bw06 text-[14px] absolute bottom-0 left-0 translate-y-[130%]'
            />
          </div>
        )}

        {moveType === MoveMoneyTypes.ACH_OUT_TRANSFER && moveMoneyData.achOutFeeData && (
          <AchOutFeeSelector
            achOutActiveFeeData={achOutActiveFeeData}
            achOutTransferType={achOutTransferType}
            setAchOutTransferType={setAchOutTransferType}
          />
        )}

        {shouldShowMemo && (
          <div className='w-[432px] mx-auto flex flex-col gap-[4px]'>
            <Heading
              title={t(reviewContentMap[moveType as keyof typeof reviewContentMap].transfer_data_title)}
              className='font-metricR text-bw08 text-[16px]'
            />
            <LiliTextArea
              onBlur={() => {
                syncTransferNote();
              }}
              onClear={() => {
                syncTransferNote(true);
              }}
              maxLength={50}
              placeholder={t(reviewContentMap[moveType as keyof typeof reviewContentMap].transfer_data_placeholder)}
              onValueChange={(value) => setTransferNote(value)}
              value={transferNote}
            />
          </div>
        )}
      </div>

      <div className='absolute bg-bw00 bg-opacity-90 left-0 bottom-0 w-full border-t border-bw03 flex items-center justify-center py-[24px]'>
        <LiliButton
          isLoading={isLoadingState}
          onClick={() => debounceAction(handleContinue)}
          disabled={!checkIfConfirmValid || !isValidForAchOut || isLoadingState}
          id='continue'
          className='w-[300px]'
          title={t('MOVE_MONEY.STEPS.REVIEW.CTA')}
        />
        {reviewContentMap[moveType as keyof typeof reviewContentMap].footer_disclaimer && (
          <Heading
            type='HTML'
            title={t(reviewContentMap[moveType as keyof typeof reviewContentMap].footer_disclaimer)}
            className='absolute right-[50px] font-metricR text-[14px] text-bw06 text-center'
          />
        )}
      </div>
    </div>
  );
};
